import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Collapse, Menu } from '@mui/material'

import { Chevron } from '@assets/svgs/Chevron'
import { MenuIcon } from '@assets/svgs/MenuIcon'
import { CompetencePeriodSelect } from './components/CompetencePeriodSelect'
import { CompetenceDateSelect } from './components/CompetenceDateSelect'
import { ClSelect } from './components/ClSelect'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { useAuthentication } from '@pages/Authentication/context'
import { usePreview } from '@pages/Preview/context'
import { ExitIcon } from '@assets/svgs/ExitIcon'
import { useLayout } from '@hooks/useLayout'
import { useUser } from '@hooks/useUser'

import type { HeaderProps, PeriodValuesProps } from './types'
import type { FieldsPersistedDataProps } from '@hooks/useLayout/types'

import userImage from '@assets/images/user-image-test.png'

import * as S from './styles'

export const Header = ({ sideBarCollapsed, setSideBarCollapsed, headerCollapsed, setHeaderCollapsed }: HeaderProps) => {
  const userBoxRef = useRef<HTMLImageElement | null>(null)
  const isInitializingRef = useRef(true)
  const previousCompetenceDateRef = useRef<string | null>(null)

  const location = useLocation()

  const { signOut, isSignedOutLoading } = useAuthentication()
  const { fieldsPersistedData, setFieldsPersistedData } = useLayout()
  const { userData, userPermissionsData, filiData, setFiliData, getCompetenceDate, competenceDateData } = useUser()
  const { periodSelectedData, setPeriodSelectedData } = usePreview()

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [filiValue, setFiliValue] = useState('')
  const [periodValue, setPeriodValue] = useState<number | null>(null)
  const [competenceDateValue, setCompetenceDateValue] = useState<string | null>(null)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)

  const isPreviewPage = useMemo(
    () =>
      ['/gestao-financeira/previa'].some((path) =>
        location.pathname.includes(path)
      ),
    [location.pathname]
  )

  const userCls = useMemo(() => {
    const cls = userData?.cls ? [...userData.cls] : []
    const clsWithoutAll = cls.filter((item) => item.filiCodigo !== 'ALL')

    if (isPreviewPage && clsWithoutAll.length > 1) {
      return [
        {
          filiCodigo: 'ALL',
          filiNome: 'Todos'
        },
        ...clsWithoutAll
      ]
    }

    return clsWithoutAll
  }, [userData, isPreviewPage])

  const handleSetFieldsPersistData = (fieldsPersistedData: FieldsPersistedDataProps) => {
    setFieldsPersistedData(fieldsPersistedData)
  }

  // Seta o centro de lucro
  const handleSetPeriodValue = ({ filiCode, competenceDate }: PeriodValuesProps) => {
    const filiName = userCls.find((item) => item.filiCodigo === filiCode)?.filiNome || ''
    const competenceEndPeriod = competenceDateData?.find(
      (item) => item.startDate === (competenceDate || competenceDateValue)
    )?.endDate

    setFiliData({
      filiCode: filiCode || filiData?.filiCode || '',
      filiName: filiName || filiData?.filiName || '',
      startPeriod: competenceDate || competenceDateValue || '',
      endPeriod: competenceEndPeriod || ''
    })
  }

  // Seta o primeiro centro de lucro
  const handleSetInitialFiliData = (filiCode?: string) => {
    const competenceEndPeriod = competenceDateData?.find((item) => item.startDate === competenceDateValue)?.endDate

    if (isPreviewPage && userCls.length > 1) {
      // Se for Preview e tiver mais de um CL, restaura o CL persistido ou usa "Todos" como padrão
      const previousFiliCode = filiCode || (fieldsPersistedData?.profitCenter as string)
      const filiDataSelected = userCls.find((item) => item.filiCodigo === previousFiliCode)
      const resolvedFiliCode = filiDataSelected?.filiCodigo || 'ALL'
      const resolvedFiliName = filiDataSelected?.filiNome || 'Todos'

      setFiliData({
        filiCode: resolvedFiliCode,
        filiName: resolvedFiliName,
        startPeriod: competenceDateValue || '',
        endPeriod: competenceEndPeriod || ''
      })
      setFiliValue(resolvedFiliCode)
    } else {
      // Se não for Preview ou tiver apenas um CL, seta o centro de lucro anterior ou o primeiro disponível
      const previousFiliCode = filiCode || (fieldsPersistedData?.profitCenter as string)
      const filiDataSelected = userCls.find((item) => item.filiCodigo === previousFiliCode) || userCls[0]

      setFiliData({
        filiCode: filiDataSelected?.filiCodigo || '',
        filiName: filiDataSelected?.filiNome || '',
        startPeriod: competenceDateValue || '',
        endPeriod: competenceEndPeriod || ''
      })
      setFiliValue(filiDataSelected?.filiCodigo || '')
    }
  }

  // Atualiza o centro de lucro quando muda de página (Preview ou outras)
  useEffect(() => {
    if (!!competenceDateValue && !!userCls?.length && !!competenceDateData?.length) {
      handleSetInitialFiliData(fieldsPersistedData?.profitCenter as string)
    }
  }, [isPreviewPage])

  // Seta o filiData inicial apenas na primeira vez que os dados estiverem prontos
  useEffect(() => {
    if (!!competenceDateValue && !!userCls?.length && !!competenceDateData?.length && isInitializingRef.current) {
      handleSetInitialFiliData(fieldsPersistedData?.profitCenter as string)
      isInitializingRef.current = false
    }
  }, [competenceDateValue, userCls, competenceDateData])

  // Mantém o select do CL sincronizado quando o filiData é alterado fora do Header (ex: "Limpar" filtros)
  useEffect(() => {
    if (filiData?.filiCode && filiData.filiCode !== filiValue) {
      setFiliValue(filiData.filiCode)
    }
  }, [filiData?.filiCode])

  // Seta a primeira data de competência
  const handleSetCompetenceDate = (date: string) => setCompetenceDateValue(date)

  useEffect(() => {
    if (!competenceDateData?.length) return

    const persistedDate = fieldsPersistedData?.competenceDate as string | undefined
    if (persistedDate) {
      handleSetCompetenceDate(persistedDate)
      handleSetPeriodValue({ competenceDate: persistedDate })

      setPeriodSelectedData({
        competencePeriod: periodSelectedData?.competencePeriod || null,
        competenceDate: persistedDate
      })
      return
    }

    const today = new Date()
    const matchingCurrentDate = competenceDateData.find((item) => {
      const start = new Date(item.startDate)
      const end = new Date(item.endDate)
      return start <= today && today <= end
    })

    // Usa o ano da string ISO (yyyy-...) para não sofrer deslocamento de fuso horário
    const getStartYear = (date: string) => date.slice(0, 4)

    // Ao trocar o período, mantém o ano que estava selecionado em vez de cair no primeiro item (ano anterior)
    const previousYear = previousCompetenceDateRef.current
      ? getStartYear(previousCompetenceDateRef.current)
      : String(today.getFullYear())
    const matchingPreviousYearDate = competenceDateData.find((item) => getStartYear(item.startDate) === previousYear)

    const defaultDate =
      matchingCurrentDate?.startDate || matchingPreviousYearDate?.startDate || competenceDateData[0].startDate
    handleSetCompetenceDate(defaultDate)
    // Atualiza filiData com a nova data para que páginas (ex: Pessoas) refaçam o fetch com o período correto
    handleSetPeriodValue({ competenceDate: defaultDate })

    setPeriodSelectedData({
      competencePeriod: periodSelectedData?.competencePeriod || null,
      competenceDate: defaultDate
    })
  }, [competenceDateData, fieldsPersistedData?.competenceDate])

  // Busca as datas de competência
  const handleGetCompetenceDate = (month: number) => getCompetenceDate(month)

  useEffect(() => {
    !!periodValue && handleGetCompetenceDate(periodValue)
  }, [periodValue])

  // Seta o primeiro período de competência
  const handleSetCompetencePeriod = (month: number) => setPeriodValue(month)

  useEffect(() => {
    const persistedPeriod = fieldsPersistedData?.competencePeriod as number | undefined

    if (persistedPeriod) {
      handleSetCompetencePeriod(persistedPeriod)

      setPeriodSelectedData({
        competencePeriod: userData?.competencePeriod?.find((item) => item.monthNumber === persistedPeriod) || null,
        competenceDate: periodSelectedData?.competenceDate || null
      })
      return
    }

    const currentMonth = new Date().getMonth() + 1
    const matchCurrent = userData?.competencePeriod?.find((item) => item.monthNumber === currentMonth)

    if (matchCurrent) {
      handleSetCompetencePeriod(matchCurrent.monthNumber)

      setPeriodSelectedData({
        competencePeriod: matchCurrent,
        competenceDate: periodSelectedData?.competenceDate || null
      })

      return
    }
  }, [fieldsPersistedData?.competencePeriod, userData?.competencePeriod])

  return (
    <>
      <S.Container>
        <S.Content>
          <S.MobileMenuButton onClick={() => setSideBarCollapsed(!sideBarCollapsed)}>
            <MenuIcon />
          </S.MobileMenuButton>

          <S.InputsBox>
            <S.SelectBox>
              <CompetencePeriodSelect
                periodValue={periodValue}
                setPeriodValue={(value) => {
                  previousCompetenceDateRef.current = competenceDateValue
                  setPeriodValue(value)
                  setCompetenceDateValue(null)
                  isInitializingRef.current = true
                  handleSetFieldsPersistData({
                    competencePeriod: value,
                    competenceDate: null,
                    profitCenter: fieldsPersistedData?.profitCenter || null
                  })

                  const competencePeriod = userData?.competencePeriod?.find((item) => item.monthNumber === value) || null

                  setPeriodSelectedData({
                    competencePeriod,
                    competenceDate: periodSelectedData?.competenceDate || null
                  })
                }}
              />
            </S.SelectBox>

            <S.SelectBox>
              <CompetenceDateSelect
                competenceDateValue={competenceDateValue}
                handleSetPeriodValue={handleSetPeriodValue}
                setCompetenceDateValue={(value) => {
                  setCompetenceDateValue(value)
                  isInitializingRef.current = false
                  handleSetFieldsPersistData({
                    competenceDate: value,
                    competencePeriod: fieldsPersistedData?.competencePeriod || null,
                    profitCenter: fieldsPersistedData?.profitCenter || null
                  })
                  setPeriodSelectedData({
                    competenceDate: value || null,
                    competencePeriod: periodSelectedData?.competencePeriod || null,
                  })
                }}
                disabled={!periodValue}
              />
            </S.SelectBox>
          </S.InputsBox>

          <S.RightContent>
            <S.SelectBox style={{ width: '300px' }}>
              <ClSelect
                filiValue={filiValue}
                userCls={userCls}
                setFiliValue={(value) => {
                  setFiliValue(value)
                  handleSetFieldsPersistData({
                    competencePeriod: fieldsPersistedData?.competencePeriod || null,
                    competenceDate: fieldsPersistedData?.competenceDate || null,
                    profitCenter: value
                  })
                }}
                handleSetPeriodValue={handleSetPeriodValue}
              />
            </S.SelectBox>

            <S.UserMenuBox>
              {/* <S.AutocompleteBox>
                <GlobalSearchSelect />
              </S.AutocompleteBox>

              <S.BellBox>
                <NotificationBell />
              </S.BellBox> */}

              <S.UserBox>
                <S.UserImage ref={userBoxRef} src={userImage} onClick={() => setIsUserMenuOpen(true)} />
              </S.UserBox>

              <Menu
                anchorEl={userBoxRef.current}
                open={isUserMenuOpen}
                onClose={() => setIsUserMenuOpen(false)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                slotProps={{
                  paper: { component: S.MenuPaper },
                  list: { component: S.MenuList }
                }}
              >
                <S.UserName>{userData?.user?.name || ''}</S.UserName>
                <S.UserEmail>{userData?.user?.email || ''}</S.UserEmail>
                <S.Separator />
                <S.UserPosition>{userPermissionsData?.profile?.name || ''}</S.UserPosition>
                <S.Separator />
                <S.LogoutButton
                  onClick={() => {
                    setIsLogoutModalOpen(true)
                    setIsUserMenuOpen(false)
                  }}
                >
                  <ExitIcon /> Sair
                </S.LogoutButton>
              </Menu>
            </S.UserMenuBox>
          </S.RightContent>
        </S.Content>

        <S.SeeMore>
          <S.SeeMoreButton onClick={() => setHeaderCollapsed(!headerCollapsed)}>
            Ver mais <Chevron />
          </S.SeeMoreButton>

          <Collapse in={headerCollapsed} timeout="auto" unmountOnExit>
            <S.CloseInputsBox>
              <CompetencePeriodSelect
                periodValue={periodValue}
                setPeriodValue={(value) => {
                  previousCompetenceDateRef.current = competenceDateValue
                  setPeriodValue(value)
                  setCompetenceDateValue(null)
                  isInitializingRef.current = true
                  handleSetFieldsPersistData({
                    competencePeriod: value,
                    competenceDate: null,
                    profitCenter: fieldsPersistedData?.profitCenter || null
                  })
                }}
              />

              <CompetenceDateSelect
                competenceDateValue={competenceDateValue}
                setCompetenceDateValue={(value) => {
                  setCompetenceDateValue(value)
                  isInitializingRef.current = false
                  handleSetFieldsPersistData({
                    competenceDate: value,
                    competencePeriod: fieldsPersistedData?.competencePeriod || null,
                    profitCenter: fieldsPersistedData?.profitCenter || null
                  })
                }}
                handleSetPeriodValue={handleSetPeriodValue}
                disabled={!periodValue}
              />

              <ClSelect
                filiValue={filiValue}
                userCls={userCls}
                setFiliValue={(value) => {
                  setFiliValue(value)
                  handleSetFieldsPersistData({
                    competencePeriod: fieldsPersistedData?.competencePeriod || null,
                    competenceDate: fieldsPersistedData?.competenceDate || null,
                    profitCenter: value
                  })
                }}
                handleSetPeriodValue={handleSetPeriodValue}
              />

              {/* <Box style={{ marginTop: 18 }}>
                <GlobalSearchSelect />
              </Box> */}
            </S.CloseInputsBox>
          </Collapse>
        </S.SeeMore>
      </S.Container>

      <ReturnModal
        open={isLogoutModalOpen}
        headerIcon={<WarningIcon />}
        title="Deseja realmente sair da plataforma?"
        message="Você terá que realizar o login novamente!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setIsLogoutModalOpen}
        onConfirm={signOut}
        isLoading={isSignedOutLoading}
      />
    </>
  )
}
